// src/pages/Invoice.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Invoice = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`/orders/${orderId}`);
        setOrder(res.data);
      } catch (error) {
        console.error('خطا در دریافت فاکتور:', error);
        alert('فاکتور یافت نشد');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const toToman = (amount) => Number(amount || 0).toLocaleString('fa-IR');

  const formatJalaliDate = (dateStr) => {
    const date = new Date(dateStr);
    const j = toJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return `${j.year}/${String(j.month).padStart(2, '0')}/${String(j.day).padStart(2, '0')}`;
  };

  const toJalali = (gy, gm, gd) => {
    let jy, jm, jd;
    gy = gy || new Date().getFullYear();
    gm = gm || new Date().getMonth() + 1;
    gd = gd || new Date().getDate();

    if (gy > 1600) {
      let gy2 = gy - 1600;
      let gm2 = gm - 1;
      let gDayNo = 365 * gy2 + Math.floor((gy2 + 3) / 4) - Math.floor((gy2 + 99) / 100) + Math.floor((gy2 + 399) / 400);
      for (let i = 0; i < gm2; ++i) gDayNo += [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][i];
      if (gm > 2 && ((gy2 % 4 === 0 && gy2 % 100 !== 0) || gy2 % 400 === 0)) gDayNo++;
      gDayNo += gd - 1;

      let jDayNo = gDayNo - 79;
      let jNp = Math.floor(jDayNo / 12053);
      jDayNo %= 12053;

      jy = 979 + 33 * jNp + 4 * Math.floor(jDayNo / 1461);
      jDayNo %= 1461;

      if (jDayNo >= 366) {
        jy += Math.floor((jDayNo - 1) / 365);
        jDayNo = (jDayNo - 1) % 365;
      }

      jm = jDayNo < 186 ? 1 + Math.floor(jDayNo / 31) : 7 + Math.floor((jDayNo - 186) / 30);
      jd = 1 + (jDayNo < 186 ? jDayNo % 31 : (jDayNo - 186) % 30);
    } else {
      jy = -14;
      jm = gm + 11;
      jd = gd;
    }

    return { year: jy, month: jm, day: jd };
  };

  const toTomanColored = (amount, isPaid = false) => {
    const str = Number(amount || 0).toLocaleString('fa-IR');
    return (
      <span className={isPaid ? 'text-green-700 font-bold' : 'text-purple-900 font-bold'}>
        {str} تومان
      </span>
    );
  };

  const handlePayInstallment = async (installment) => {
    if (installment.status === 'paid') return;

    // چک قسط قبلی پرداخت شده باشه
    const prevInst = order.installments.find(i => i.installment_number === installment.installment_number - 1);
    if (installment.installment_number > 1 && (!prevInst || prevInst.status !== 'paid')) {
      alert('قسط قبلی هنوز کامل پرداخت نشده است');
      return;
    }

    if (!window.confirm(`آیا مطمئن هستید قسط ${installment.installment_number} را کامل پرداخت کنید؟`)) {
      return;
    }

    try {
      const res = await axios.post('/payment/test/installment/pay', {
        installmentId: installment.id
      });

      if (res.data.success) {
        alert(`قسط ${installment.installment_number} با موفقیت کامل پرداخت شد!`);
        window.location.reload(); // رفرش صفحه برای بروزرسانی وضعیت
      }
    } catch (error) {
      alert(error.response?.data?.message || 'خطا در پرداخت قسط');
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    const element = document.getElementById('invoice');
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const width = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * width) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, width, height);
    pdf.save(`فاکتور-${order.order_number}.pdf`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-2xl text-gold-dark">در حال بارگذاری فاکتور...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <p className="text-2xl text-red-600">فاکتور یافت نشد</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 print:bg-white print:p-0">
      <div className="max-w-5xl mx-auto">
        <div id="invoice" className="bg-white rounded-3xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none">
          {/* هدر طلایی */}
          <div className="bg-gradient-to-r from-gold to-gold-dark text-white py-10 px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold">فاکتور رسمی خرمی‌گلد</h1>
            <p className="text-xl mt-4">شماره فاکتور: <span className="font-mono text-2xl">{order.order_number}</span></p>
            <p className="text-lg mt-2">تاریخ صدور: {new Date(order.created_at).toLocaleDateString('fa-IR')}</p>
          </div>

          {/* وضعیت پرداخت */}
          <div className="text-center my-10">
            <span className={`inline-block px-10 py-5 rounded-full text-white text-2xl font-bold shadow-lg ${
              order.status === 'pre_paid' ? 'bg-green-600' : 'bg-orange-600'
            }`}>
              {order.status === 'pre_paid' ? 'پیش‌پرداخت پرداخت شده' : 'در انتظار پرداخت'}
            </span>
          </div>

          {/* محصولات */}
          <div className="px-6 pb-12 space-y-12">
            {order.items.map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
                {/* عکس و مشخصات */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                  <div className="md:col-span-1">
                    <img
                      src={item.images?.[0]?.image_url || '/placeholder.jpg'}
                      alt={item.product_name}
                      className="w-full h-64 object-cover rounded-2xl shadow-md"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">{item.product_name}</h2>
                    <div className="grid grid-cols-2 gap-4 text-lg">
                      <p><span className="font-medium">بارکد:</span> {item.barcode}</p>
                      <p><span className="font-medium">وزن:</span> {item.weight} گرم</p>
                      <p><span className="font-medium">نوع کالا:</span> {item.noe_kala || 'نامشخص'}</p>
                      <p><span className="font-medium">درصد ساخت:</span> {item.darsad || '۰'}%</p>
                    </div>
                  </div>
                </div>

                {/* جزئیات قیمت */}
                <div className="bg-white rounded-2xl p-6 shadow-inner">
                  <h3 className="text-xl font-bold text-gray-800 text-center mb-6">جزئیات محاسبه قیمت</h3>
                  <div className="space-y-4 text-lg">
                    <div className="flex justify-between"><span>هر گرم طلا:</span> <span className="font-bold">{toToman(item.current_gold_price)} تومان</span></div>
                    <div className="flex justify-between"><span>طلای خام:</span> <span className="font-bold">{toToman(item.raw_gold_price)} تومان</span></div>
                    <div className="flex justify-between"><span>اجرت ساخت:</span> <span className="font-bold">{toToman(item.ojrat_total)} تومان</span></div>

                    {/* سود فروشگاه */}
                    <div className="flex justify-between py-4 bg-gray-100 rounded-xl px-6">
                      <span className="font-medium text-xl">سود فروشگاه:</span>
                      <span className="font-bold text-xl">
                        {item.payment_type === 'cash' ? (
                          <span className="text-green-700">
                            {item.sood_percent}% ({toToman(item.effective_profit_amount)} تومان)
                          </span>
                        ) : (
                          <span className="text-purple-700">
                            {item.profit_percent}% (اقساطی — بدون سود اضافی)
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between"><span>مالیات:</span> <span className="font-bold">{toToman(item.vat_amount)} تومان</span></div>
                    <div className="flex justify-between border-t-2 pt-4 font-bold text-xl">
                      <span>قیمت قبل از تخفیف:</span>
                      <span>{toToman(item.price_before_discount)} تومان</span>
                    </div>
                    {item.discount_percent > 0 && (
                      <div className="flex justify-between text-green-600 font-bold text-xl">
                        <span>تخفیف ({item.discount_percent}%):</span>
                        <span>-{toToman(item.price_before_discount - item.price)} تومان</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t-4 border-gold pt-6 text-3xl font-bold text-gold-dark">
                      <span>قیمت نهایی:</span>
                      <span>{toToman(item.price)} تومان</span>
                    </div>
                  </div>
                </div>

                {/* بخش اقساطی */}
{/* بخش اقساطی */}
{item.payment_type === 'installment' && item.months > 0 && (
  <div className="mt-10 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-8 border-2 border-purple-300">
    <h3 className="text-2xl font-bold text-purple-900 text-center mb-8">
      خرید اقساطی — {item.months} ماهه
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mb-10">
      <div className="bg-white rounded-xl p-6 shadow">
        <p className="text-gray-600 mb-2">پیش‌پرداخت</p>
        <p className="text-2xl font-bold text-purple-800">{toToman(item.pre_payment)} تومان</p>
      </div>
      <div className="bg-purple-100 rounded-xl p-6">
        <p className="text-gray-700 mb-2">بدهی کل</p>
        <p className="text-3xl font-bold text-purple-900">{Number(item.gold_debt).toFixed(3)} گرم</p>
      </div>
      <div className="bg-white rounded-xl p-6 shadow">
        <p className="text-gray-600 mb-2">هر قسط (تقریبی)</p>
        <p className="text-2xl font-bold text-purple-900">
          {(item.gold_debt / item.months).toFixed(3)} گرم
        </p>
      </div>
    </div>

    {/* جدول اقساط — نسخه نهایی */}
    <div className="bg-white rounded-2xl p-6 shadow-inner">
      <p className="text-center text-xl font-bold text-purple-900 mb-6">جدول پرداخت اقساط</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm md:text-base border-collapse">
        <thead>
  <tr className="bg-purple-100 text-purple-900">
    <th className="p-4 border border-purple-200">قسط</th>
    <th className="p-4 border border-purple-200">سررسید</th>
    <th className="p-4 border border-purple-200">وزن بدهی</th>
    <th className="p-4 border border-purple-200">مبلغ تقریبی / پرداختی</th>
    <th className="p-4 border border-purple-200">قیمت طلا در پرداخت</th>
    <th className="p-4 border border-purple-200">تاریخ پرداخت</th>
    <th className="p-4 border border-purple-200">وضعیت</th>
    <th className="p-4 border border-purple-200 print:hidden">عملیات</th>
  </tr>
</thead>
<tbody>
  {(order.installments || [])
    .filter(inst => inst.order_item_id === item.id)
    .map(inst => {
      const isPreviousPaid = inst.installment_number === 1 || 
        order.installments.some(prev => 
          prev.installment_number === inst.installment_number - 1 && prev.status === 'paid'
        );

      // مبلغ به تومان (با مدیریت مقادیر خالی)
      let amountDisplay;
      if (inst.status === 'paid') {
        const paidAmt = Number(inst.paid_amount) || 0;
        amountDisplay = paidAmt > 0 
          ? toTomanColored(paidAmt, true)
          : <span className="text-gray-500">—</span>;
      } else {
        // تقریبی با قیمت فعلی (اگر current_due_amount نباشه، محاسبه دستی)
        const currentPrice = Number(order.current_gold_price) ; // قیمت پیش‌فرض
        const approxAmount = Number(inst.due_gold_weight) * currentPrice;
        amountDisplay = toTomanColored(approxAmount);
      }

      // قیمت طلا در پرداخت
      const goldPrice = inst.status === 'paid' 
        ? (Number(inst.paid_gold_price) > 0 ? toToman(inst.paid_gold_price) : '—')
        : toToman(order.current_gold_price );

      // تاریخ پرداخت
      const paidDate = inst.paid_at 
        ? formatJalaliDate(inst.paid_at)
        : '—';

      return (
        <tr key={inst.id} className="hover:bg-purple-50 transition">
          <td className="p-4 text-center border border-purple-100 font-medium">
            {inst.installment_number}
          </td>
          <td className="p-4 text-center border border-purple-100">
            {formatJalaliDate(inst.due_date)}
          </td>
          <td className="p-4 text-center border border-purple-100">
            {Number(inst.due_gold_weight).toFixed(3)} گرم
          </td>
          <td className="p-4 text-center border border-purple-100 font-medium">
            {amountDisplay}
          </td>
          <td className="p-4 text-center border border-purple-100">
            {goldPrice}
          </td>
          <td className="p-4 text-center border border-purple-100">
            {paidDate}
          </td>
          <td className="p-4 text-center border border-purple-100">
            <span className={`px-4 py-2 rounded-full text-white font-bold ${
              inst.status === 'paid' ? 'bg-green-600' : 'bg-red-600'
            }`}>
              {inst.status === 'paid' ? 'پرداخت شده ✓' : 'در انتظار'}
            </span>
          </td>
          <td className="p-4 text-center border border-purple-100 print:hidden">
            {inst.status === 'paid' ? (
              <span className="text-green-600 font-bold flex items-center justify-center gap-2">
                پرداخت شده <span className="text-2xl">✓</span>
              </span>
            ) : isPreviousPaid ? (
              <button
                onClick={() => handlePayInstallment(inst)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition shadow flex items-center gap-2 mx-auto"
              >
                پرداخت کامل قسط
                <span className="text-sm bg-white/20 px-2 py-1 rounded">
                  {toToman(inst.current_due_amount || (inst.due_gold_weight * 16000000))}
                </span>
              </button>
            ) : (
              <button
                disabled
                className="bg-gray-400 text-white px-6 py-3 rounded-xl font-medium cursor-not-allowed opacity-70 flex items-center gap-2 mx-auto"
              >
                پرداخت کامل قسط
                <span className="text-sm bg-white/20 px-2 py-1 rounded">
                  {toToman(inst.current_due_amount || (inst.due_gold_weight * 16000000))}
                </span>
              </button>
            )}
          </td>
        </tr>
      );
    })}
</tbody>
        </table>
      </div>
    </div>
  </div>
)}
              </div>
            ))}
          </div>

          {/* جمع کل */}
          <div className="bg-gradient-to-r from-gold-light to-gold text-white py-12 px-8 text-center">
            <p className="text-2xl mb-4">جمع پیش‌پرداخت پرداخت‌شده</p>
            <p className="text-5xl md:text-6xl font-bold">{toToman(order.total_pre_payment)} تومان</p>
            {order.total_gold_debt > 0 && (
              <p className="text-3xl md:text-4xl mt-8">
                بدهی باقی‌مانده: <span className="font-bold">{Number(order.total_gold_debt).toFixed(3)} گرم طلا</span>
              </p>
            )}
          </div>

          {/* دکمه‌ها */}
          <div className="text-center py-12 space-y-6 md:space-y-0 md:space-x-8 print:hidden">
            <button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-5 px-12 rounded-2xl text-xl transition shadow-2xl transform hover:scale-105"
            >
              چاپ فاکتور
            </button>
            <button
              onClick={handleDownloadPDF}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-5 px-12 rounded-2xl text-xl transition shadow-2xl transform hover:scale-105"
            >
              دانلود PDF
            </button>
            <Link
              to="/profile/orders"
              className="inline-block bg-gold hover:bg-gold-dark text-white font-bold py-5 px-12 rounded-2xl text-xl transition shadow-2xl transform hover:scale-105"
            >
              مشاهده همه فاکتورها
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;